
import React from "react";
import Image from "@/components/AppImage/AppImage";
import Button from "../Buttons/Button/Button";
import glob from "../../assets/glob.svg";
import locationPin from "../../assets/location_pin.svg";
import "./CreatorsCard.css";
import { TRIP_TYPES_UI } from "@/constants/tripTypes";
import { useNavigate } from 'react-router-dom';
import { useAvatarImage } from "@/hooks/useAvatarImage";
import { useHeroImage, ImageChainMetadata } from "@/hooks/useHeroImage";
import { useChainMetadataCallback } from "@/hooks/useChainMetadataCallback";
import { debug } from '@/utils/logger';
import { useAuth } from "@/contexts";
import { hrefForUserProfile } from "@/utils/username";

interface Props {
  title?: string;
  city?: string;
  followers?: number;
  countries?: number;
  recs?: number;
  authorAvatar?: string;
  authorAvatarSecondary?: string;
  authorAvatarTertiary?: string;
  authorAvatarQuaternary?: string;
  heroImage?: string;
  heroImageSecondary?: string;
  onFollowClick?: () => void;
  traveller_types?: string[];
  isFollowing?: boolean;
  id?: string;
  /**
   * Profile type of the card's subject. When 'creator' or 'advisor', the
   * fallback navigation emits `/storefront/<id>` (brand term of art); other
   * values (or omitted) emit `/profile/<id>`. Callers rendering a specific
   * role grid (Creators / Advisors) should pass this; mixed-role grids
   * (Results) should pass each person's own profile_type.
   */
  profile_type?: string;
  /**
   * Username handle for the card's subject. When present it's used in place
   * of `id` in the fallback href so the URL reads as `/storefront/<handle>`
   * instead of `/storefront/<mongo_id>` — matches the ProfilePage
   * canonicalization and avoids an id→handle round-trip.
   */
  username?: string;
  onCardClick?: (id?: string) => void;
  onViewProfile?: (id?: string) => void;
  isCurrentUser?: boolean;
  lazy?: boolean; // If true, only validate images when card is visible
  trackChain?: boolean; // If true, track and return image validation chain results
  onChainMetadata?: (heroChain: ImageChainMetadata | null, avatarChain: ImageChainMetadata | null) => void;
}

const CreatorsCard: React.FC<Props> = ({
  title = "Default Title",
  city = "Default City",
  followers,
  countries = 5,
  recs = 21,
  authorAvatar = "",
  authorAvatarSecondary = "",
  authorAvatarTertiary = "",
  authorAvatarQuaternary = "",
  heroImage = "",
  heroImageSecondary = "",
  onFollowClick = () => debug("Remix clicked"),
  traveller_types = [],
  isFollowing = false,
  id,
  profile_type,
  username,
  onCardClick,
  onViewProfile,
  isCurrentUser = false,
  lazy = false,
  trackChain = false,
  onChainMetadata,
}) => {
  const { user } = useAuth();
  const router = useNavigate();
  
  const heroImageResult = useHeroImage({
    heroImage,
    heroImageSecondary,
    lazy,
    trackChain,
  });

  const avatarImageResult = useAvatarImage({
    largeAvatar: authorAvatar,
    smallAvatar: authorAvatarSecondary,
    avatarUrl: authorAvatarTertiary,
    profileImageUrl: authorAvatarQuaternary,
    lazy,
    trackChain,
  });

  // Extract chain metadata if available
  const heroChainMetadata = (typeof heroImageResult === 'object' && 'chainMetadata' in heroImageResult)
    ? heroImageResult.chainMetadata
    : null;
  const avatarChainMetadata = (typeof avatarImageResult === 'object' && 'chainMetadata' in avatarImageResult)
    ? avatarImageResult.chainMetadata
    : null;

  // Handle chain metadata callback notifications
  useChainMetadataCallback(onChainMetadata, trackChain, heroChainMetadata, avatarChainMetadata);

  // Handle both lazy and non-lazy return types
  const finalHeroImage = typeof heroImageResult === 'string' 
    ? heroImageResult 
    : heroImageResult.resolvedHeroImage;
  const heroContainerRef = typeof heroImageResult === 'object' && 'containerRef' in heroImageResult
    ? heroImageResult.containerRef 
    : undefined;

  const finalAvatarImage = typeof avatarImageResult === 'string'
    ? avatarImageResult
    : avatarImageResult.resolvedAvatarImage;
  const avatarContainerRef = typeof avatarImageResult === 'object' && 'containerRef' in avatarImageResult
    ? avatarImageResult.containerRef
    : undefined;

  // Use a ref callback that applies to both containers if lazy
  const cardRef = React.useCallback<React.RefCallback<HTMLDivElement>>((element) => {
    if (lazy && element) {
      // Apply refs to the card element for intersection observer
      if (heroContainerRef) heroContainerRef(element);
      if (avatarContainerRef) avatarContainerRef(element);
    }
  }, [lazy, heroContainerRef, avatarContainerRef]);

  const heroBackgroundImage = `linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0) 38%), url(${finalHeroImage})`;

  const handleCardClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Check if clicking on interactive elements (buttons, links)
    const target = event.target as HTMLElement;
    const interactiveRoot = target.closest(
      ".content-card-actions, button, a, [role='button']"
    );

    if (interactiveRoot && interactiveRoot !== event.currentTarget) {
      return; // Let interactive elements handle their own clicks
    }

    if (onCardClick) {
      event.preventDefault();
      onCardClick(id);
      return;
    }

    // Use onViewProfile callback if provided (from parent component)
    // This allows parent to handle popover rendering at a higher level
    if (onViewProfile) {
      event.preventDefault();
      onViewProfile(id);
      return;
    }

    // Fallback: direct navigation (for logged-in users or if no callback provided).
    // Creators and advisors land on /storefront/<handle> (brand term of art);
    // travelers (or when profile_type is omitted) land on /profile/<handle>.
    router(hrefForUserProfile({ _id: id, username, profile_type }));
  }, [onCardClick, onViewProfile, id, username, profile_type, router]);

  return (
    <div
      className="content-card"
      ref={cardRef}
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      <div
        className="content-card-hero"
        style={{ backgroundImage: heroBackgroundImage }}
      ></div>
      <div className="creators-card-body">
        <div className="content-card-author">
          <div className="content-card-author-avatar-wrapper" style={{ position: "relative" }}>
            <Image
              className="content-card-author-avatar"
              src={finalAvatarImage}
              alt="author avatar"
              fill
              style={{ objectFit: "cover" }}
            />
          </div>

          <div className="content-card-author-actions">
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                if (user) {
                  onFollowClick();
                }
                // For logged-out users, button shows "Sign up to follow" but doesn't trigger action
                // since they can't view profiles anyway
              }}
              className="content-card-follow-button manrope"
            >
              {user 
                ? (isFollowing ? "Unfollow" : "Follow")
                : "Sign up to follow"}
            </Button>
          </div>
        </div>
        <div className="content-card-content">
          <h4 className="content-card-title manrope">{title}</h4>
          <div className="content-card-subtitle">{city}</div>
          {isCurrentUser && <span className="current-user-badge">You</span>}
          <div className="content-card-subtitle">
            {followers === 0 || followers === 1
              ? `${followers} follower`
              : `${followers} followers`}
          </div>
        </div>
        <div className="traveller-types-container">
          {traveller_types &&
            traveller_types.map((type, index) => {
              const normalizedType = type?.toString?.() ?? "";
              const trip = TRIP_TYPES_UI.find(
                (t) =>
                  normalizedType &&
                  (t.id === normalizedType.toLowerCase() || t.label === normalizedType)
              );
              return (
                <Button
                  key={index}
                  variant="ghost"
                  startIcon={trip?.icon}
                  onClick={() => debug("Traveller type clicked", normalizedType)}
                  className="creators-card-btn manrope"
                >
                  {trip?.label || normalizedType}
                </Button>
              );
            })}
          {/* <Button
                        startIcon={<Image src={food} height={16} width={16} alt="food" />}
                        onClick={onRemixClick}
                        className="creators-card-btn"
                    >
                        Foodie
                    </Button>
                    <Button
                        startIcon={<Image src={pin} height={16} width={16} alt="pin" />}
                        onClick={onRemixClick}
                        className="creators-card-btn"
                    >
                        Culture
                    </Button> */}
        </div>
        <div className="content-card-footer">
          {/* <div className="content-card-chip">
                        <span className="content-card-chip-text">{chipText}</span>
                    </div> */}
          <div className="content-card-metrics">
            <div className="content-card-metric">
              <Image src={glob} alt="globe" width={16} height={16} className="" />
              <span>{countries !== undefined ? `${countries} countries` : '...'}</span>
            </div>
            <div className="content-card-metric">
              <Image src={locationPin} alt="recs" width={16} height={16} />
              <span>{recs} recs</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorsCard;
